import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@gokrew/ui';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@gokrew/ui/components/tabs';
import { LogOut, Trash2, UserPlus } from 'lucide-react';
import {
  getCrmMembers,
  inviteCrmMember,
  removeCrmMember,
  setCrmDealsVisibility,
  updateCrmProfile,
  type CrmDealsVisibility,
} from '@/api/crm';
import { getMyAdminProfile } from '@/api/admins';
import { changePassword } from '@/api/auth';
import { useAuth } from '@/context/AuthContext';
import { personInitials, personName, useCrmFormat } from './crm-format';
import { CrmButton, CrmRequired } from './CrmShared';
import {
  CRM_MAIL_APPS,
  readMailApp,
  saveMailApp,
  type CrmMailApp,
} from './crm-attention';
import styles from './Crm.module.css';

type Status = { kind: 'ok' | 'error'; text: string } | null;
const statusOf = (err: unknown) =>
  (err as { response?: { status?: number } }).response?.status;

const Feedback = ({ status }: { status: Status }) =>
  status ? (
    <p
      role={status.kind === 'error' ? 'alert' : 'status'}
      className={status.kind === 'error' ? styles.error : styles.successText}
    >
      {status.text}
    </p>
  ) : null;

/** The signed-in member's settings: profile, password, language and team. */
export const CrmSettings = () => {
  const { t, i18n } = useCrmFormat();
  const { user, refreshUser, logout } = useAuth();
  const client = useQueryClient();
  const profile = useQuery({
    queryKey: ['crm', 'me'],
    queryFn: getMyAdminProfile,
  });
  const me = profile.data?.data?.user;
  const [names, setNames] = useState({ firstName: '', lastName: '' });
  const [language, setLanguage] = useState<'en' | 'fr'>(
    i18n.language === 'fr' ? 'fr' : 'en'
  );
  useEffect(() => {
    if (!me) return;
    setNames({ firstName: me.firstName ?? '', lastName: me.lastName ?? '' });
    if (me.language === 'fr' || me.language === 'en') setLanguage(me.language);
  }, [me]);
  const [mailApp, setMailApp] = useState<CrmMailApp | null>(readMailApp);
  const [busy, setBusy] = useState(false);
  const [profileStatus, setProfileStatus] = useState<Status>(null);
  const saveProfile = async (next: {
    firstName: string;
    lastName: string;
    language: 'en' | 'fr';
  }) => {
    setBusy(true);
    setProfileStatus(null);
    try {
      await updateCrmProfile(next);
      await i18n.changeLanguage(next.language);
      await Promise.all([
        refreshUser(),
        client.invalidateQueries({ queryKey: ['crm'] }),
      ]);
      setProfileStatus({ kind: 'ok', text: t('crm.settings.saved') });
    } catch {
      setProfileStatus({ kind: 'error', text: t('crm.save_error') });
    } finally {
      setBusy(false);
    }
  };

  const [password, setPassword] = useState({
    currentPassword: '',
    newPassword: '',
    confirm: '',
  });
  const [passwordStatus, setPasswordStatus] = useState<Status>(null);
  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (password.newPassword !== password.confirm) {
      setPasswordStatus({
        kind: 'error',
        text: t('crm.settings.password_mismatch'),
      });
      return;
    }
    setBusy(true);
    setPasswordStatus(null);
    try {
      await changePassword({
        currentPassword: password.currentPassword,
        newPassword: password.newPassword,
      });
      setPassword({ currentPassword: '', newPassword: '', confirm: '' });
      setPasswordStatus({
        kind: 'ok',
        text: t('crm.settings.password_changed'),
      });
    } catch (err) {
      setPasswordStatus({
        kind: 'error',
        text: t(
          [400, 401].includes(statusOf(err) ?? 0)
            ? 'crm.settings.password_wrong'
            : 'crm.save_error'
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
  });
  const myDealsVisibility: CrmDealsVisibility =
    members.data?.find((member) => member.userId === user?.id)
      ?.dealsVisibility ?? 'team';
  const [visibilityStatus, setVisibilityStatus] = useState<Status>(null);
  const chooseDealsVisibility = async (value: CrmDealsVisibility) => {
    setBusy(true);
    setVisibilityStatus(null);
    try {
      await setCrmDealsVisibility(value);
      await client.invalidateQueries({ queryKey: ['crm'] });
      setVisibilityStatus({ kind: 'ok', text: t('crm.settings.saved') });
    } catch {
      setVisibilityStatus({ kind: 'error', text: t('crm.save_error') });
    } finally {
      setBusy(false);
    }
  };
  const [invite, setInvite] = useState({
    email: '',
    firstName: '',
    lastName: '',
  });
  const [teamStatus, setTeamStatus] = useState<Status>(null);
  const submitInvite = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setTeamStatus(null);
    try {
      await inviteCrmMember({
        email: invite.email.trim(),
        firstName: invite.firstName.trim(),
        lastName: invite.lastName.trim(),
      });
      setTeamStatus({
        kind: 'ok',
        text: t('crm.settings.invited', { email: invite.email.trim() }),
      });
      setInvite({ email: '', firstName: '', lastName: '' });
      await members.refetch();
    } catch (err) {
      setTeamStatus({
        kind: 'error',
        text: t(
          statusOf(err) === 400
            ? 'crm.settings.invite_domain'
            : 'crm.settings.invite_error'
        ),
      });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (userId: string) => {
    setBusy(true);
    setTeamStatus(null);
    try {
      await removeCrmMember(userId);
      await members.refetch();
    } catch {
      setTeamStatus({ kind: 'error', text: t('crm.settings.remove_error') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tabs defaultValue="profile" className={styles.settings}>
      <TabsList>
        <TabsTrigger value="profile">{t('crm.settings.profile')}</TabsTrigger>
        <TabsTrigger value="password">{t('crm.settings.password')}</TabsTrigger>
        <TabsTrigger value="language">
          {t('crm.settings.preferences')}
        </TabsTrigger>
        <TabsTrigger value="team">{t('crm.settings.team')}</TabsTrigger>
      </TabsList>

      <TabsContent value="profile">
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            void saveProfile({ ...names, language });
          }}
        >
          <fieldset className={styles.grid} disabled={busy || !me}>
            {(['firstName', 'lastName'] as const).map((key) => (
              <label key={key}>
                <span>
                  {t(`crm.contact_${key}`)}
                  <CrmRequired />
                </span>
                <Input
                  required
                  maxLength={100}
                  value={names[key]}
                  onChange={(event) =>
                    setNames((old) => ({ ...old, [key]: event.target.value }))
                  }
                />
              </label>
            ))}
            <label className={styles.fullWidth}>
              {t('crm.contact_email')}
              <Input value={user?.email ?? ''} readOnly disabled />
            </label>
          </fieldset>
          <Feedback status={profileStatus} />
          <div className={styles.actions}>
            <CrmButton
              type="submit"
              variant="adminPrimary"
              disabled={
                busy || !names.firstName.trim() || !names.lastName.trim()
              }
            >
              {t(busy ? 'crm.saving' : 'crm.save')}
            </CrmButton>
          </div>
        </form>
      </TabsContent>

      <TabsContent value="password">
        <form className={styles.form} onSubmit={submitPassword}>
          <fieldset className={styles.grid} disabled={busy}>
            {(['currentPassword', 'newPassword', 'confirm'] as const).map(
              (key) => (
                <label key={key} className={styles.fullWidth}>
                  <span>
                    {t(`crm.settings.${key}`)}
                    <CrmRequired />
                  </span>
                  <Input
                    required
                    type="password"
                    minLength={8}
                    autoComplete={
                      key === 'currentPassword'
                        ? 'current-password'
                        : 'new-password'
                    }
                    value={password[key]}
                    onChange={(event) =>
                      setPassword((old) => ({
                        ...old,
                        [key]: event.target.value,
                      }))
                    }
                  />
                </label>
              )
            )}
          </fieldset>
          <p className={styles.muted}>{t('crm.settings.password_rule')}</p>
          <Feedback status={passwordStatus} />
          <div className={styles.actions}>
            <CrmButton type="submit" variant="adminPrimary" disabled={busy}>
              {t('crm.settings.change_password')}
            </CrmButton>
          </div>
        </form>
      </TabsContent>

      <TabsContent value="language">
        <div className={styles.form}>
          <div
            className={styles.languageChoice}
            role="radiogroup"
            aria-label={t('crm.settings.language')}
          >
            {(['fr', 'en'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={language === value}
                className={language === value ? styles.languageActive : ''}
                disabled={busy || !me}
                onClick={() => {
                  setLanguage(value);
                  void saveProfile({ ...names, language: value });
                }}
              >
                {t(`crm.settings.languages.${value}`)}
              </button>
            ))}
          </div>
          <p className={styles.muted}>{t('crm.settings.language_hint')}</p>
          <Feedback status={profileStatus} />
          <div
            className={styles.languageChoice}
            role="radiogroup"
            aria-label={t('crm.settings.mail_app')}
          >
            {CRM_MAIL_APPS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mailApp === value}
                className={mailApp === value ? styles.languageActive : ''}
                onClick={() => {
                  setMailApp(value);
                  saveMailApp(value);
                }}
              >
                {t(`crm.attention.mail_apps.${value}`)}
              </button>
            ))}
          </div>
          <p className={styles.muted}>{t('crm.settings.mail_app_hint')}</p>
          <div
            className={styles.languageChoice}
            role="radiogroup"
            aria-label={t('crm.settings.deals_visibility')}
          >
            {(['team', 'private'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={myDealsVisibility === value}
                className={
                  myDealsVisibility === value ? styles.languageActive : ''
                }
                disabled={busy || !members.data}
                onClick={() => void chooseDealsVisibility(value)}
              >
                {t(`crm.settings.deals_visibility_options.${value}`)}
              </button>
            ))}
          </div>
          <p className={styles.muted}>
            {t('crm.settings.deals_visibility_hint')}
          </p>
          <Feedback status={visibilityStatus} />
        </div>
      </TabsContent>

      <TabsContent value="team">
        <ul className={styles.teamList}>
          {(members.data ?? []).map((member) => (
            <li key={member.userId}>
              <span className={styles.smallAvatar}>
                {personInitials(member)}
              </span>
              <span className={styles.teamName}>
                <strong>{personName(member)}</strong>
                <small>{member.email}</small>
              </span>
              {member.userId !== user?.id && (
                <CrmButton
                  variant="ghost"
                  IconPrefix={Trash2}
                  aria-label={t('crm.settings.remove', {
                    name: personName(member),
                  })}
                  disabled={busy}
                  onClick={() => void remove(member.userId)}
                />
              )}
            </li>
          ))}
        </ul>
        <form className={styles.form} onSubmit={submitInvite}>
          <h3 className={styles.settingsHeading}>
            {t('crm.settings.invite_title')}
          </h3>
          <fieldset className={styles.grid} disabled={busy}>
            {(['firstName', 'lastName'] as const).map((key) => (
              <label key={key}>
                <span>
                  {t(`crm.contact_${key}`)}
                  <CrmRequired />
                </span>
                <Input
                  required
                  maxLength={100}
                  value={invite[key]}
                  onChange={(event) =>
                    setInvite((old) => ({ ...old, [key]: event.target.value }))
                  }
                />
              </label>
            ))}
            <label className={styles.fullWidth}>
              <span>
                {t('crm.contact_email')}
                <CrmRequired />
              </span>
              <Input
                required
                type="email"
                maxLength={254}
                placeholder={t('crm.settings.invite_placeholder')}
                value={invite.email}
                onChange={(event) =>
                  setInvite((old) => ({ ...old, email: event.target.value }))
                }
              />
            </label>
          </fieldset>
          <p className={styles.muted}>{t('crm.settings.invite_hint')}</p>
          <Feedback status={teamStatus} />
          <div className={styles.actions}>
            <CrmButton
              type="submit"
              variant="adminPrimary"
              IconPrefix={UserPlus}
              disabled={busy}
            >
              {t('crm.settings.invite')}
            </CrmButton>
          </div>
        </form>
      </TabsContent>
      <div className={styles.settingsFooter}>
        <CrmButton
          type="button"
          variant="outline"
          IconPrefix={LogOut}
          onClick={() => void logout()}
        >
          {t('crm.logout')}
        </CrmButton>
      </div>
    </Tabs>
  );
};
